import React from 'react';
type TCPAVariant = 'checkbox' | 'no-interaction';
interface TCPADisclosureProps {
  /**
   * Which Jornaya implementation to render.
   *
   * - `checkbox` (default): consent is gated by an opt-in checkbox, and that
   *   checkbox carries the `leadid_tcpa_disclosure` id so Jornaya records the
   *   interaction alongside the text and visibility.
   * - `no-interaction`: consent is granted by submitting the form, so a hidden
   *   input carries the id and no checkbox is rendered.
   */
  variant?: TCPAVariant;
  /** Ignored when variant is `no-interaction`. */
  checked?: boolean;
  /** Ignored when variant is `no-interaction`. */
  onChange?: (checked: boolean) => void;
  error?: string;
  /** The disclosure language. Captured verbatim on the certificate. */
  children: React.ReactNode;
  /** Tailwind classes for the checkbox, so each school keeps its accent color. */
  checkboxClassName?: string;
  textClassName?: string;
}

/**
 * TCPA consent block with Jornaya (Verisk LeadiD) tagging.
 *
 * Jornaya locates the element whose id is exactly `leadid_tcpa_disclosure`,
 * then reads the disclosure language and measures its on-screen visibility from
 * the `<label for="leadid_tcpa_disclosure">` pointing at it. Without that
 * pairing the certificate returns `tcpa.disclosure` and `tcpa.visibility`
 * missing, and buyers reject the lead.
 *
 * Which element carries the id depends on how consent is captured:
 *
 * - An opt-in checkbox gates consent -> the id goes on the CHECKBOX, so the
 *   affirmative interaction is certified too.
 * - Submitting the form IS the consent -> the id goes on a hidden input.
 *
 * Using the hidden-input form on a page that actually has a checkbox
 * understates what was captured, which is why this is a real distinction and
 * not two ways of spelling the same thing.
 *
 * The label must contain ONLY the disclosure language, so the checkbox is a
 * sibling rather than nested inside it.
 *
 * Render at most one of these per page — the id must be unique.
 */
export function TCPADisclosure({
  variant = 'checkbox',
  checked = false,
  onChange,
  error,
  children,
  checkboxClassName = 'mt-0.5 h-4 w-4 rounded border-gray-300 cursor-pointer flex-shrink-0',
  textClassName = 'text-xs text-gray-600 leading-relaxed'
}: TCPADisclosureProps) {
  // No-interaction: nothing to tick, so the disclosure is body copy and the id
  // lives on a hidden input.
  if (variant === 'no-interaction') {
    return <div className="pt-2" data-id="element-922" data-mp="26xuywncktimu">
        <input id="leadid_tcpa_disclosure" type="hidden" data-id="element-923" data-mp="2by98i9ttt0mz" />
        <label htmlFor="leadid_tcpa_disclosure" className={textClassName} data-id="element-924" data-mp="2bwof4nj8znqp">
          {children}
        </label>
      </div>;
  }

  // Checkbox: the checkbox itself is the tagged element. `htmlFor` both points
  // Jornaya at the disclosure text and gives the label native click-to-toggle,
  // so no onClick handler is needed.
  return <div className="space-y-2 pt-2" data-id="element-925" data-mp="1t7rhqask0f49">
      <div className="flex items-start gap-2.5" data-id="element-926" data-mp="1t6q6a57mlqm0">
        <input id="leadid_tcpa_disclosure" name="tcpa-consent" type="checkbox" checked={checked} onChange={e => onChange?.(e.target.checked)} className={checkboxClassName} data-id="element-927" data-mp="acwqs6qnwsj4" />
        <label htmlFor="leadid_tcpa_disclosure" className={`${textClassName} cursor-pointer`} data-id="element-928" data-mp="1b757hjgont9">
          {children}
        </label>
      </div>
      {error && <p className="text-xs text-red-600" data-id="element-929" data-mp="3nkqf107b217b">{error}</p>}
    </div>;
}